import { useCallback, useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { api, type Thread } from '../api.ts';
import { Chat } from '../components/Chat.tsx';

/**
 * The pupil's line to their teacher.
 *
 * No recipient to choose: there is exactly one thread, and whichever teacher is around answers it.
 * A child stuck on homework should be one click from asking, not deciding who to address.
 *
 * Polled while the page is open. An answer arriving a few seconds late costs nothing here, and a
 * socket would be a connection to nurse for a difference nobody would notice.
 */
const POLL_MS = 15_000;

export function MessagesPage(): JSX.Element {
  const [thread, setThread] = useState<Thread | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      setThread(await api.myThread());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить переписку');
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <>
      <div className="page-head">
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
        <h1>Вопрос преподавателю</h1>
        <p className="muted">
          Напишите, если что-то непонятно в задании или уроке. Ответит преподаватель вашей школы.
        </p>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {thread === null && !error ? <p className="muted">Загружаем…</p> : null}

      {thread ? (
        <Chat
          messages={thread.messages}
          mineAreFromStudent
          placeholder="Например: не понимаю, как сделать цикл в третьем задании"
          onSend={async (body) => {
            const sent = await api.sendMyMessage(body);
            // Shown at once rather than waiting for the next poll: the person who just pressed
            // send needs to see it land.
            setThread((current) =>
              current ? { ...current, messages: [...current.messages, sent] } : current,
            );
          }}
        />
      ) : null}
    </>
  );
}
